import * as SecureStore from 'expo-secure-store';
import * as React from 'react';
import { Platform } from 'react-native';

import { RELEASE_FEATURE_FLAG_MANIFEST, type ReleaseFeature } from '@/lib/features';

const STORAGE_KEY = 'sixam.dev-release-features.v1';

type Listener = () => void;

export type DevReleaseFeatureValues = Record<ReleaseFeature, boolean>;

export type DevReleaseFeatureState = Readonly<{
  hydrated: boolean;
  values: DevReleaseFeatureValues;
}>;

export function createDisabledDevReleaseFeatures(): DevReleaseFeatureValues {
  return Object.fromEntries(
    RELEASE_FEATURE_FLAG_MANIFEST.map(([feature]) => [feature, false])
  ) as DevReleaseFeatureValues;
}

function parsePersistedFeatures(value: string | null): DevReleaseFeatureValues {
  const features = createDisabledDevReleaseFeatures();
  if (!value) return features;

  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return features;

    const enabledFeatures = new Set(
      parsed.filter((item): item is string => typeof item === 'string')
    );
    for (const [feature] of RELEASE_FEATURE_FLAG_MANIFEST) {
      features[feature] = enabledFeatures.has(feature);
    }
  } catch {
    return features;
  }

  return features;
}

async function readPersistedFeatures(): Promise<DevReleaseFeatureValues> {
  if (!__DEV__) return createDisabledDevReleaseFeatures();

  try {
    let value: string | null = null;
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') value = window.localStorage.getItem(STORAGE_KEY);
    } else {
      value = await SecureStore.getItemAsync(STORAGE_KEY);
    }
    return parsePersistedFeatures(value);
  } catch {
    return createDisabledDevReleaseFeatures();
  }
}

async function persistFeatures(features: DevReleaseFeatureValues): Promise<void> {
  if (!__DEV__) return;

  const enabledFeatures = RELEASE_FEATURE_FLAG_MANIFEST.flatMap(([feature]) =>
    features[feature] ? [feature] : []
  );
  const value = JSON.stringify(enabledFeatures);

  try {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, value);
    } else {
      await SecureStore.setItemAsync(STORAGE_KEY, value);
    }
  } catch {
    // Persistence must never prevent development feature controls from working in memory.
  }
}

export class DevReleaseFeatureStore {
  private state: DevReleaseFeatureState;
  private readonly listeners = new Set<Listener>();

  constructor(initialValues = createDisabledDevReleaseFeatures(), hydrated = false) {
    this.state = { hydrated, values: initialValues };
  }

  getSnapshot = (): DevReleaseFeatureState => this.state;

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  replace(values: DevReleaseFeatureValues, persist = true) {
    this.state = { hydrated: true, values };
    for (const listener of this.listeners) listener();
    if (persist) void persistFeatures(values);
  }

  setFeature(feature: ReleaseFeature, enabled: boolean) {
    this.replace({ ...this.state.values, [feature]: enabled });
  }

  setAll(enabled: boolean) {
    const values = createDisabledDevReleaseFeatures();
    if (enabled) {
      for (const [feature] of RELEASE_FEATURE_FLAG_MANIFEST) values[feature] = true;
    }
    this.replace(values);
  }

  async hydrate() {
    this.replace(await readPersistedFeatures(), false);
  }
}

export const devReleaseFeatureStore = new DevReleaseFeatureStore(
  createDisabledDevReleaseFeatures(),
  !__DEV__
);

export function useDevReleaseFeatureState(store = devReleaseFeatureStore): DevReleaseFeatureState {
  return React.useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

export function DevReleaseFeatureProvider({ children }: Readonly<React.PropsWithChildren>) {
  React.useEffect(() => {
    if (__DEV__) void devReleaseFeatureStore.hydrate();
  }, []);

  return children;
}
